import ol from 'openlayers'
import { AnyAction } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { bottomBarHeightMargin, profileLegendWidth } from 'src/Constants'
import { DataAction, EmptyAction, MessageAction } from 'src/CustomTypes'
import { CrossSection, ProfileObject } from 'src/dataSources/geo/CrossSection'
import { RootState } from 'src/Store'
import { getSelectedHydroModelLayers } from 'src/utils/MapLayerUtils'
import { ProfileSetting, ProfileView } from '../reducers/ProfileReducer'

export const CLEAR_PROFILE_STATE = 'PROFILE_ACTIONS_CLEAR_PROFILE_STATE'
export const CURRENT_PROFILE_VIEW = 'PROFILE_ACTIONS_CURRENT_PROFILE_VIEW'
export const DISABLE_SETTING = 'PROFILE_ACTIONS_DISABLE_SETTING'
export const ENABLE_SETTING = 'PROFILE_ACTIONS_ENABLE_SETTING'
export const FETCHING_PROFILE_STARTED =
  'PROFILE_ACTIONS_FETCHING_PROFILE_STARTED'
export const FETCHING_PROFILE_SUCCESS =
  'PROFILE_ACTIONS_FETCHING_PROFILE_SUCCESS'
export const FETCHING_PROFILE_FAILURE =
  'PROFILE_ACTIONS_FETCHING_PROFILE_FAILURE'
export const SET_COORDINATES = 'PROFILE_ACTIONS_SET_COORDINATES'
export const SET_MAX_DEPTH = 'PROFILE_ACTIONS_SET_MAX_DEPTH'
export const SET_MAX_BOREHOLE_DISTANCE =
  'PROFILE_ACTIONS_SET_MAX_BOREHOLE_DISTANCE'
export const SET_CROSSHAIR_DISTANCE = 'PROFILE_ACTIONS_SET_CROSSHAIR_DISTANCE'

interface ProfileActions {
  clearState: () => EmptyAction
  currentView: (viewType: ProfileView) => DataAction<ProfileView>
  disableSetting: (setting: ProfileSetting) => DataAction<ProfileSetting>
  enableSetting: (setting: ProfileSetting) => DataAction<ProfileSetting>
  fetchingProfileStarted: () => EmptyAction
  fetchingProfileFailure: (message: string) => MessageAction
  fetchingProfileSuccess: (profile: ProfileObject) => DataAction<ProfileObject>
  setCoordinates: (coordinates: ol.Coordinate[]) => DataAction<ol.Coordinate[]>
  setMaxBoreholeDistance: (distance: number) => DataAction<number>
  setMaxDepth: (depth: number) => DataAction<number>
  setCrosshairDistance: (distance: number | undefined) => DataAction<number | undefined>

  // Thunk actions
  fetchProfile: () => ThunkAction<Promise<void>, RootState, {}, AnyAction>
}

export const profileActions: ProfileActions = {
  clearState: () => {
    return {
      type: CLEAR_PROFILE_STATE
    }
  },
  currentView: (viewType: ProfileView): DataAction<ProfileView> => {
    return {
      type: CURRENT_PROFILE_VIEW,
      data: viewType
    }
  },
  disableSetting: (setting: ProfileSetting): DataAction<ProfileSetting> => {
    return {
      type: DISABLE_SETTING,
      data: setting
    }
  },
  enableSetting: (setting: ProfileSetting): DataAction<ProfileSetting> => {
    return {
      type: ENABLE_SETTING,
      data: setting
    }
  },
  fetchingProfileStarted: (): EmptyAction => {
    return {
      type: FETCHING_PROFILE_STARTED
    }
  },
  fetchingProfileFailure: (message: string): MessageAction => {
    return {
      type: FETCHING_PROFILE_FAILURE,
      message: message
    }
  },
  fetchingProfileSuccess: (borehole: ProfileObject): DataAction<ProfileObject> => {
    return {
      type: FETCHING_PROFILE_SUCCESS,
      data: borehole
    }
  },
  setMaxBoreholeDistance: (distance: number) => {
    return {
      type: SET_MAX_BOREHOLE_DISTANCE,
      data: distance
    }
  },
  setCoordinates: (coordinates: ol.Coordinate[]): DataAction<ol.Coordinate[]> => {
    return {
      type: SET_COORDINATES,
      data: coordinates
    }
  },
  setMaxDepth: (depth: number) => {
    return {
      type: SET_MAX_DEPTH,
      data: depth
    }
  },
  setCrosshairDistance: (distance: number | undefined): DataAction<number | undefined> => {
    return {
      type: SET_CROSSHAIR_DISTANCE,
      data: distance
    }
  },
  fetchProfile: (): ThunkAction<Promise<void>, RootState, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<RootState, {}, AnyAction>, getState: () => RootState): Promise<void> => {
      // Fetch profile
      const token = getState().authState.token
      dispatch(profileActions.fetchingProfileStarted())

      const model = getState().generalState.selectedGeoModel
      const coordinates = getState().profileState.coordinates
      if (coordinates === undefined) {
        return
      }

      const bottomBarWidth = getState().generalState.bottomBarWidth
      const bottomBarHeight = getState().generalState.bottomBarHeight
      const maxDepth = getState().profileState.maxDepth
      const maxBoreholeDistance = getState().profileState.maxBoreholeDistance

      const wsCrossSection = new CrossSection(token)
      if (model !== undefined) {
        wsCrossSection.setModelId(model.ID)
      } else {
        wsCrossSection.setModelId('-1')
      }
      wsCrossSection.setPathCoordinates(coordinates)
      wsCrossSection.setHydroModelIds(getSelectedHydroModelLayers(getState().mapState))
      wsCrossSection.setMaxDepth(maxDepth)
      wsCrossSection.setMaxBoreholeDistance(maxBoreholeDistance)
      wsCrossSection.setWidth(bottomBarWidth - profileLegendWidth)
      wsCrossSection.setHeight(bottomBarHeight - bottomBarHeightMargin)
      try {
        const profile = await wsCrossSection.fetchProfile()
        dispatch(profileActions.fetchingProfileSuccess(profile))
      } catch (err) {
        console.error(err)
        const errorMessage: string = (err as Error).message
        dispatch(profileActions.fetchingProfileFailure(errorMessage))
      }
    }
  }
}
